import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Moon } from "lucide-react";
import { useAuth } from "../../lib/auth";
import RavenFortune from "./RavenFortune";

const NAV = [
  { to: "/", label: "Tavern Entrance", end: true, testid: "nav-home" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/adventures", label: "Adventures", testid: "nav-adventures" },
  { to: "/ravens", label: "Ravens", testid: "nav-ravens" },
  { to: "/star-charts", label: "Star Charts", testid: "nav-star-charts" },
  { to: "/second-life", label: "Second Life", testid: "nav-second-life" },
  { to: "/wolf-grid", label: "Wolf Grid", testid: "nav-wolf-grid" },
  { to: "/radio", label: "Radio", testid: "nav-radio" },
  { to: "/gallery", label: "Gallery", testid: "nav-gallery" },
  { to: "/notice-board", label: "Notice Board", testid: "nav-notice-board" },
  { to: "/guest-book", label: "Guest Book", testid: "nav-guest-book" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" aria-hidden="true" />

      <header
        data-testid="tavern-nav"
        className="sticky top-0 z-40 backdrop-blur-xl"
        style={{
          background: "rgba(26,10,46,0.78)",
          borderBottom: "1px solid rgba(212,175,55,0.22)",
          boxShadow: "0 4px 32px rgba(0,0,0,0.4)",
        }}
      >
        <div className="mx-auto flex max-w-[1240px] items-center justify-between px-5 py-4 md:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="relative h-10 w-10 rounded-full border" style={{borderColor:"var(--gold)"}}>
              <Moon className="absolute inset-0 m-auto h-5 w-5" style={{color:"var(--gold)"}} />
              <div className="absolute inset-0 rounded-full" style={{boxShadow:"0 0 18px rgba(212,175,55,0.45)"}} />
            </div>
            <div className="leading-tight">
              <div className="tavern-heading text-xl gold-text">Nocturne Orchid</div>
              <div className="tavern-script text-xs" style={{color:"var(--orchid-light)"}}>where curiosity blooms after dark</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({isActive}) => `nav-link ${isActive ? "active" : ""}`} data-testid={n.testid}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            {user ? (
              <Link to="/admin" className="gold-btn" data-testid="admin-link">Keeper's Quarters</Link>
            ) : (
              <Link to="/admin" className="gold-btn" data-testid="admin-link">Sign In</Link>
            )}
          </div>

          <button className="xl:hidden text-[color:var(--gold)] p-2" onClick={() => setOpen(o => !o)} data-testid="nav-toggle" aria-label="Toggle navigation">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden border-t" style={{borderColor:"rgba(212,175,55,0.22)"}}>
            <nav className="mx-auto max-w-[1240px] px-5 py-4 grid gap-3">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} className={({isActive}) => `nav-link ${isActive ? "active" : ""}`} data-testid={`m-${n.testid}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/admin" className="gold-btn justify-center mt-2" data-testid="m-admin-link">
                {user ? "Keeper's Quarters" : "Sign In"}
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main className="relative z-10">
        <Outlet />
      </main>

      <footer className="relative z-10 mt-16 border-t" style={{borderColor:"rgba(212,175,55,0.18)"}}>
        <div className="mx-auto max-w-[1240px] px-6 py-12 md:flex md:items-center md:justify-between gap-6">
          <div>
            <div className="tavern-heading text-2xl gold-text">Nocturne Orchid</div>
            <p className="text-sm mt-2" style={{color:"var(--muted)"}}>
              A cozy fantasy tavern of stories, adventures, and curiosities.
            </p>
          </div>
          <div className="mt-6 md:mt-0 tavern-script text-lg" style={{color:"var(--orchid-light)"}}>
            ✦  the lanterns are always lit  ✦
          </div>
        </div>
        <div className="text-center pb-6 text-xs" style={{color:"var(--muted)"}}>
          © {new Date().getFullYear()} Nocturne Orchid · Built by candlelight
          <span className="mx-2" aria-hidden="true">·</span>
          <Link to="/welcome" className="underline hover:text-[color:var(--gold)] transition-colors" data-testid="footer-welcome-link">
            Read the Tavern's Welcome
          </Link>
        </div>
      </footer>

      <RavenFortune />
    </div>
  );
}
